import axios from 'axios';
import { IDomain } from '../../types';
import { API_CONFIG, STORAGE_KEYS } from '../../constants';

const SERVER_URL = API_CONFIG.SERVER_URL;

// Create axios instance with default config
const api = axios.create({
  baseURL: SERVER_URL,
});

// Add request interceptor to include token in headers
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid, redirect to login
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER);
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

interface DomainResponse {
  domains: IDomain[];
  domain: IDomain;
}

// Get all domains (common endpoint)
export const getDomains = async (): Promise<DomainResponse> => {
  try {
    const response = await api.get<DomainResponse>('domain/');
    return response.data;
  } catch (error) {
    console.error('Error fetching domains:', error);
    throw error;
  }
};

// Get single domain by name (common endpoint)
export const getDomain = async (
  domainName: string
): Promise<DomainResponse> => {
  try {
    const response = await api.get<DomainResponse>(`domain/${domainName}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching domain:', error);
    throw error;
  }
};

// Create domain (Mailgun specific)
export const createDomain = async (
  domainData: Partial<IDomain>
): Promise<IDomain> => {
  try {
    const response = await api.post<IDomain>('mailgun/domain/', domainData);
    return response.data;
  } catch (error: any) {
    console.error('Error creating domain:', error);
    throw (
      error.response.data?.message?.details ||
      error.response.data?.message ||
      error.message ||
      'Failed to create domain'
    );
  }
};

// Verify domain (Mailgun specific)
export const verifyDomain = async (domainId: string): Promise<IDomain> => {
  try {
    const response = await api.get<IDomain>(
      `mailgun/domain/${domainId}/verify`
    );
    return response.data;
  } catch (error) {
    console.error('Error verifying domain:', error);
    throw error;
  }
};

// Delete domain (Mailgun specific)
export const deleteDomain = async (domainName: string): Promise<void> => {
  try {
    await api.delete(`mailgun/domain/${domainName}`);
  } catch (error) {
    console.error('Error deleting domain:', error);
    throw error;
  }
};

export default api;
